import { useCallback, useId, useRef, useState, type FormEvent, type MutableRefObject, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import {
  emptiedAllowlistWarning,
  expiryForSubmit,
  formatIPLines,
  parseIPLines,
  parseRateLimit,
  toDateTimeLocal,
} from '@/lib/apiKeyRules';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import { usePolledData, type PolledData } from '@/lib/hooks/usePolledData';
import type { APIKey, CreateAPIKeyRequest, CreateAPIKeyResponse } from '@/types/apiKey';
import type { Catalog, CatalogStack } from '@/types/catalog';
import type { DomainSummary } from '@/types/domain';

interface ApiKeyFormDialogProps {
  open: boolean;
  /** null = create; a key = edit its rules (no secret reveal). */
  editing: APIKey | null;
  onOpenChange: (open: boolean) => void;
  /** Create succeeded; the parent closes this dialog and reveals the key. */
  onCreated: (created: CreateAPIKeyResponse) => void;
  /** Edit succeeded. */
  onSaved: () => void;
}

/** Shell always mounted; the form mounts only while open so fields and errors reset on every open. */
export function ApiKeyFormDialog({ open, editing, onOpenChange, onCreated, onSaved }: ApiKeyFormDialogProps) {
  const submittingRef = useRef(false);
  const { captureOpener, restoreFocus } = useDialogFocusRestore();
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submittingRef.current) return;
        onOpenChange(next);
      }}
    >
      {open ? (
        <FormBody
          key={editing ? `edit:${editing.id}` : 'create'}
          editing={editing}
          onClose={() => onOpenChange(false)}
          onCreated={onCreated}
          onSaved={onSaved}
          submittingRef={submittingRef}
          captureOpener={captureOpener}
          restoreFocus={restoreFocus}
        />
      ) : null}
    </Dialog>
  );
}

interface FormState {
  name: string;
  expires_at: string; // datetime-local value, browser time zone
  own_apps_only: boolean;
  rate_limit: string; // '' = unlimited
  ips: string; // textarea, one per line
  domain_ids: number[];
  stack_ids: number[];
}

// New keys see only their own apps; the server default (and existing keys) stay unrestricted.
const EMPTY_FORM: FormState = { name: '', expires_at: '', own_apps_only: true, rate_limit: '', ips: '', domain_ids: [], stack_ids: [] };

function formFromKey(key: APIKey): FormState {
  return {
    name: key.name,
    expires_at: toDateTimeLocal(key.expires_at),
    own_apps_only: key.own_apps_only,
    rate_limit: String(key.rate_limit_rpm ?? ''),
    ips: formatIPLines(key.ip_allowlist),
    domain_ids: key.allowed_domain_ids,
    stack_ids: key.allowed_catalog_stack_ids,
  };
}

interface RuleOptions {
  domains: DomainSummary[];
  /** Catalogs that have stacks, each with its stacks. */
  catalogs: { catalog: Catalog; stacks: CatalogStack[] }[];
}

async function loadRuleOptions(): Promise<RuleOptions> {
  const [domains, catalogs] = await Promise.all([api.domains.list(), api.catalog.list()]);
  const groups = await Promise.all(catalogs.map(async (catalog) => ({ catalog, stacks: await api.catalog.stacks.list(catalog.id) })));
  return { domains, catalogs: groups.filter((g) => g.stacks.length > 0) };
}

/** The key as stored now, so an edit never starts from a stale table row. */
async function loadKey(id: number): Promise<APIKey> {
  const key = (await api.apiKeys.list()).find((k) => k.id === id);
  if (!key) throw new Error('This key no longer exists');
  return key;
}

function toggleID(ids: number[], id: number, on: boolean): number[] {
  return on ? (ids.includes(id) ? ids : [...ids, id]) : ids.filter((x) => x !== id);
}

const checkboxClass = 'size-4 accent-primary outline-none focus-visible:ring-2 focus-visible:ring-ring';
const textareaClass =
  'min-h-[80px] w-full rounded-lg border border-input bg-transparent px-2.5 py-2 font-mono text-xs placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none dark:bg-input/30';
const listBoxClass = 'max-h-48 space-y-2 overflow-y-auto rounded-lg border border-input p-2';

function CheckItem({ checked, onChange, children }: { checked: boolean; onChange: (on: boolean) => void; children: ReactNode }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm select-none">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className={checkboxClass} />
      <span className="min-w-0 [overflow-wrap:anywhere]">{children}</span>
    </label>
  );
}

interface FormBodyProps {
  editing: APIKey | null;
  onClose: () => void;
  onCreated: (created: CreateAPIKeyResponse) => void;
  onSaved: () => void;
  submittingRef: MutableRefObject<boolean>;
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}

const statusAlertClass = 'flex flex-wrap items-center justify-between gap-3 rounded-lg bg-destructive/10 p-2 text-sm text-destructive';

function FormBody({ editing, onClose, onCreated, onSaved, submittingRef, captureOpener, restoreFocus }: FormBodyProps) {
  const ids = useId();
  const formId = `${ids}-form`;
  const cancelRef = useRef<HTMLButtonElement>(null);
  const rateRef = useRef<HTMLInputElement>(null);
  // Edit: the form is seeded from a fresh read of the key, never from the table row.
  const current = usePolledData(() => (editing ? loadKey(editing.id) : Promise.resolve(null)));
  const [form, setForm] = useState<FormState | null>(editing ? null : EMPTY_FORM);
  if (form === null && current.data) setForm(formFromKey(current.data));
  const base = editing ? (current.data ?? null) : null;
  const [submitting, setSubmitting] = useState(false);
  // Server errors and the emptied-allowlist warning: one alert at the top. The rate limit check: a message under the field.
  const [error, setError] = useState<string | null>(null);
  const [rateError, setRateError] = useState<string | null>(null);
  // The emptied-allowlist warning already shown; submitting again with the same lists goes through.
  const [confirmedWarning, setConfirmedWarning] = useState<string | null>(null);
  const options = usePolledData(loadRuleOptions);

  const alertRef = useCallback((el: HTMLParagraphElement | null) => {
    el?.focus();
  }, []);

  const change = (patch: Partial<FormState>) => setForm((f) => (f ? { ...f, ...patch } : f));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current || !form) return;
    const rate = parseRateLimit(form.rate_limit);
    if (rate === undefined) {
      setError(null);
      setRateError('Rate limit must be a whole number from 1 to 1,000,000, or empty');
      rateRef.current?.focus();
      return;
    }
    setRateError(null);
    const body: CreateAPIKeyRequest = {
      name: form.name,
      expires_at: expiryForSubmit(form.expires_at, base?.expires_at ?? null),
      own_apps_only: form.own_apps_only,
      rate_limit_rpm: rate,
      ip_allowlist: parseIPLines(form.ips),
      allowed_domain_ids: form.domain_ids,
      allowed_catalog_stack_ids: form.stack_ids,
    };
    const warning = base ? emptiedAllowlistWarning(base, body) : null;
    if (warning && warning !== confirmedWarning) {
      setConfirmedWarning(warning);
      setError(warning);
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      if (editing) {
        await api.apiKeys.update(editing.id, body);
        toast.success('API key updated');
        onSaved();
      } else {
        const created = await api.apiKeys.create(body);
        onCreated(created); // parent closes this dialog and opens the one-time reveal
      }
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : editing ? 'Failed to update API key' : 'Failed to create API key');
      setSubmitting(false);
    } finally {
      submittingRef.current = false;
    }
  };

  const blockWhileSubmitting = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  const loadState = (data: Pick<PolledData<unknown>, 'loading' | 'error' | 'refetch'>, what: string) =>
    data.loading ? (
      <p role="status" className="text-sm text-muted-foreground">
        Loading…
      </p>
    ) : (
      <div role="alert" className={statusAlertClass}>
        <span>{data.error ?? `Failed to load ${what}`}</span>
        <Button type="button" size="sm" variant="outline" onClick={data.refetch}>
          Retry
        </Button>
      </div>
    );

  // Allowlisted ids missing from the loaded lists (a deleted catalog stack keeps its entry).
  // They stay listed so the admin sees them and can uncheck them.
  const knownStackIDs = new Set(options.data?.catalogs.flatMap((g) => g.stacks.map((st) => st.id)));
  const missingStackIDs = form && options.data ? form.stack_ids.filter((id) => !knownStackIDs.has(id)) : [];
  const knownDomainIDs = new Set(options.data?.domains.map((d) => d.id));
  const missingDomainIDs = form && options.data ? form.domain_ids.filter((id) => !knownDomainIDs.has(id)) : [];

  return (
    <DialogContent
      className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        cancelRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={blockWhileSubmitting}
      onPointerDownOutside={blockWhileSubmitting}
      onInteractOutside={blockWhileSubmitting}
    >
      <DialogHeader>
        <DialogTitle>{editing ? 'Edit API key' : 'Create API key'}</DialogTitle>
        <DialogDescription>
          {editing ? 'Changes apply from the next request made with this key.' : 'The key is displayed once after creation.'}
        </DialogDescription>
      </DialogHeader>
      {!form ? (
        loadState(current, 'the API key')
      ) : (
        <form id={formId} onSubmit={(e) => void submit(e)} className="space-y-4">
          {error ? (
            <p ref={alertRef} tabIndex={-1} role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive outline-none">
              {error}
            </p>
          ) : null}

          <div className="space-y-2">
            <label htmlFor={`${ids}-name`} className="text-sm font-medium">
              Name
            </label>
            <Input id={`${ids}-name`} value={form.name} onChange={(e) => change({ name: e.target.value })} required placeholder="SaaS production" />
          </div>

          <div className="space-y-2">
            <label htmlFor={`${ids}-expires`} className="text-sm font-medium">
              Expires at (optional)
            </label>
            <Input
              id={`${ids}-expires`}
              type="datetime-local"
              value={form.expires_at}
              onChange={(e) => change({ expires_at: e.target.value })}
            />
          </div>

          <fieldset className="space-y-1" aria-describedby={`${ids}-rules-hint`}>
            <legend className="text-sm font-medium">Rules</legend>
            <p id={`${ids}-rules-hint`} className="text-xs text-muted-foreground">
              Empty lists allow everything.
            </p>

            <div className="space-y-4 pt-3">
              <CheckItem checked={form.own_apps_only} onChange={(on) => change({ own_apps_only: on })}>
                Only apps created by this key
              </CheckItem>

              <div className="space-y-2">
                <label htmlFor={`${ids}-rate`} className="text-sm font-medium">
                  Rate limit (requests per minute)
                </label>
                <Input
                  ref={rateRef}
                  id={`${ids}-rate`}
                  inputMode="numeric"
                  value={form.rate_limit}
                  onChange={(e) => {
                    change({ rate_limit: e.target.value });
                    setRateError(null);
                  }}
                  placeholder="Unlimited"
                  {...(rateError ? { 'aria-invalid': true, 'aria-describedby': `${ids}-rate-error` } : {})}
                />
                {rateError ? (
                  <p id={`${ids}-rate-error`} className="text-xs text-destructive">
                    {rateError}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <label htmlFor={`${ids}-ips`} className="text-sm font-medium">
                  Allowed IPs / CIDRs
                </label>
                <textarea
                  id={`${ids}-ips`}
                  value={form.ips}
                  onChange={(e) => change({ ips: e.target.value })}
                  placeholder="203.0.113.0/24"
                  spellCheck={false}
                  aria-describedby={`${ids}-ips-hint`}
                  className={textareaClass}
                />
                <p id={`${ids}-ips-hint`} className="text-xs text-muted-foreground">
                  One per line.
                </p>
              </div>

              {!options.data ? (
                loadState(options, 'domains and catalog stacks')
              ) : (
                <>
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">Allowed domains</legend>
                    {options.data.domains.length + missingDomainIDs.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No domains configured.</p>
                    ) : (
                      <div className={listBoxClass}>
                        {options.data.domains.map((d) => (
                          <CheckItem
                            key={d.id}
                            checked={form.domain_ids.includes(d.id)}
                            onChange={(on) => change({ domain_ids: toggleID(form.domain_ids, d.id, on) })}
                          >
                            {d.name}
                            {d.is_default ? <span className="text-muted-foreground"> (default)</span> : null}
                          </CheckItem>
                        ))}
                        {missingDomainIDs.map((id) => (
                          <CheckItem key={id} checked onChange={(on) => change({ domain_ids: toggleID(form.domain_ids, id, on) })}>
                            <span className="text-muted-foreground">Domain #{id} (not listed)</span>
                          </CheckItem>
                        ))}
                      </div>
                    )}
                    <p aria-live="polite" className="text-xs text-muted-foreground">
                      {form.domain_ids.length === 0 ? 'None selected: all domains allowed' : ''}
                    </p>
                  </fieldset>

                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">Allowed catalog stacks</legend>
                    {options.data.catalogs.length + missingStackIDs.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No catalog stacks yet.</p>
                    ) : (
                      <div className={listBoxClass}>
                        {options.data.catalogs.map(({ catalog, stacks }) => (
                          <div key={catalog.id} role="group" aria-labelledby={`${ids}-catalog-${catalog.id}`} className="space-y-1">
                            <div id={`${ids}-catalog-${catalog.id}`} className="text-xs font-medium text-muted-foreground">
                              {catalog.name}
                            </div>
                            {stacks.map((st) => (
                              <CheckItem
                                key={st.id}
                                checked={form.stack_ids.includes(st.id)}
                                onChange={(on) => change({ stack_ids: toggleID(form.stack_ids, st.id, on) })}
                              >
                                {st.name}
                              </CheckItem>
                            ))}
                          </div>
                        ))}
                        {missingStackIDs.map((id) => (
                          <CheckItem key={id} checked onChange={(on) => change({ stack_ids: toggleID(form.stack_ids, id, on) })}>
                            <span className="text-muted-foreground">Stack #{id} (not listed)</span>
                          </CheckItem>
                        ))}
                      </div>
                    )}
                    <p aria-live="polite" className="text-xs text-muted-foreground">
                      {form.stack_ids.length === 0 ? 'None selected: all catalog stacks allowed' : ''}
                    </p>
                  </fieldset>
                </>
              )}
            </div>
          </fieldset>
        </form>
      )}

      {/* Outside the form so Cancel, which takes focus on open, stays mounted while the key loads. */}
      <DialogFooter className="gap-2 sm:gap-0">
        <Button ref={cancelRef} type="button" variant="outline" aria-disabled={submitting} onClick={() => !submitting && onClose()}>
          Cancel
        </Button>
        <Button type="submit" form={formId} aria-disabled={submitting || !form} aria-busy={submitting}>
          {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
          {submitting ? (editing ? 'Saving…' : 'Creating…') : editing ? 'Save' : 'Create'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
